import {Delete as DeleteIcon, Edit as EditIcon, StarBorder} from '@mui/icons-material';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {FC, useMemo, useState, useCallback} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {useDbData} from '@website_frontend/lib/db_cache';
import {
  getSearches,
  deleteSearch,
  setCurrentSearchId,
  getCurrentSearchId,
  type SavedSearch,
  type SearchFilters,
} from '@website_frontend/lib/user_data_storage';

export const SearchesPage: FC = () => {
  const [, setLocation] = useLocation();
  const {data} = useDbData();
  const [searches, setSearches] = useState<SavedSearch[]>(getSearches());
  const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<SavedSearch | null>(null);

  const currentSearchId = getCurrentSearchId();

  const handleApplySearch = useCallback(
    (search: SavedSearch) => {
      setCurrentSearchId(search.id);
      // Navigate to home with search filters applied
      const params = new URLSearchParams();
      const {filters} = search;

      if (filters.priceMin !== undefined) params.set('priceMin', String(filters.priceMin));
      if (filters.priceMax !== undefined) params.set('priceMax', String(filters.priceMax));
      if (filters.surfaceMin !== undefined) params.set('surfaceMin', String(filters.surfaceMin));
      if (filters.surfaceMax !== undefined) params.set('surfaceMax', String(filters.surfaceMax));
      if (filters.bedroomsMin !== undefined) params.set('bedroomsMin', String(filters.bedroomsMin));
      if (filters.bedroomsMax !== undefined) params.set('bedroomsMax', String(filters.bedroomsMax));
      if (filters.roomsMin !== undefined) params.set('roomsMin', String(filters.roomsMin));
      if (filters.roomsMax !== undefined) params.set('roomsMax', String(filters.roomsMax));
      if (filters.landSurfaceMin !== undefined)
        params.set('landSurfaceMin', String(filters.landSurfaceMin));
      if (filters.landSurfaceMax !== undefined)
        params.set('landSurfaceMax', String(filters.landSurfaceMax));
      if (filters.floorNbMin !== undefined) params.set('floorNbMin', String(filters.floorNbMin));
      if (filters.floorNbMax !== undefined) params.set('floorNbMax', String(filters.floorNbMax));
      if (filters.energyCertificateMin !== undefined)
        params.set('energyCertificateMin', filters.energyCertificateMin);
      if (filters.energyCertificateMax !== undefined)
        params.set('energyCertificateMax', filters.energyCertificateMax);
      if (filters.typeLabel && filters.typeLabel.length > 0) {
        for (const typeLabel of filters.typeLabel) {
          params.append('typeLabel', typeLabel);
        }
      }

      setLocation(`/?${params.toString()}`);
    },
    [setLocation]
  );

  const handleDelete = useCallback((search: SavedSearch) => {
    if (search.isDefault) return; // Can't delete default
    deleteSearch(search.id);
    setSearches(getSearches());
    setDeleteConfirm(null);
  }, []);

  const handleRename = useCallback((search: SavedSearch) => {
    setEditingSearch(search);
    setEditName(search.name);
  }, []);

  const handleSaveRename = useCallback(() => {
    if (!(editingSearch && editName.trim())) return;

    // Update localStorage
    const updatedSearch = {...editingSearch, name: editName.trim()};
    const allSearches = getSearches();
    const index = allSearches.findIndex(s => s.id === editingSearch.id);
    if (index >= 0) {
      allSearches[index] = updatedSearch;
      localStorage.setItem('searches', JSON.stringify(allSearches));
    }

    setSearches(getSearches());
    setEditingSearch(null);
    setEditName('');
  }, [editingSearch, editName]);

  const handleSetDefault = useCallback((search: SavedSearch) => {
    const allSearches = getSearches();
    const updated = allSearches.map(s => ({
      ...s,
      isDefault: s.id === search.id,
    }));
    localStorage.setItem('searches', JSON.stringify(updated));
    setSearches(updated);
  }, []);

  const handleCreateEmptySearch = useCallback(() => {
    const empty: SearchFilters = {};
    const baseName = 'Nouvelle recherche';
    // Ensure unique name by appending a number if necessary
    const existingNames = new Set(getSearches().map(s => s.name));
    let name = baseName;
    let idx = 2;
    while (existingNames.has(name)) {
      name = `${baseName} ${idx}`;
      idx += 1;
    }
    const newSearch = {id: '', name, filters: empty, lastUsed: Date.now()} as SavedSearch;
    // Use provided helper to persist
    const stored = JSON.parse(localStorage.getItem('searches') ?? '[]');
    const id = `search_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    newSearch.id = id;
    stored.push(newSearch);
    localStorage.setItem('searches', JSON.stringify(stored));
    setSearches(getSearches());
  }, []);

  const getFilterSummary = (filters: SearchFilters): string[] => {
    const summary: string[] = [];
    if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
      const min = filters.priceMin?.toLocaleString() ?? '';
      const max = filters.priceMax?.toLocaleString() ?? '';
      if (min && max) summary.push(`Prix: ${min} - ${max} €`);
      else if (min) summary.push(`Prix min: ${min} €`);
      else if (max) summary.push(`Prix max: ${max} €`);
    }
    if (filters.surfaceMin !== undefined || filters.surfaceMax !== undefined) {
      const min = filters.surfaceMin ?? '';
      const max = filters.surfaceMax ?? '';
      if (min && max) summary.push(`Surface: ${min} - ${max} m²`);
      else if (min) summary.push(`Surface min: ${min} m²`);
      else if (max) summary.push(`Surface max: ${max} m²`);
    }
    if (filters.typeLabel && filters.typeLabel.length > 0) {
      summary.push(`Types: ${filters.typeLabel.join(', ')}`);
    }
    if (summary.length === 0) summary.push('Aucun filtre');
    return summary;
  };

  const getResultCount = (filters: SearchFilters): number => {
    if (!data) return 0;
    // Simple filter matching (same logic as HomePage)
    const matchingIds = data.filter(item => {
      const itemDpeValue = item.energyCertificate
        ? ['A', 'B', 'C', 'D', 'E', 'F', 'G'].indexOf(item.energyCertificate.toUpperCase())
        : undefined;
      const minDpeValue = filters.energyCertificateMin
        ? ['A', 'B', 'C', 'D', 'E', 'F', 'G'].indexOf(filters.energyCertificateMin.toUpperCase())
        : undefined;
      const maxDpeValue = filters.energyCertificateMax
        ? ['A', 'B', 'C', 'D', 'E', 'F', 'G'].indexOf(filters.energyCertificateMax.toUpperCase())
        : undefined;

      return (
        (filters.priceMin === undefined ||
          (item.price !== undefined && item.price >= filters.priceMin)) &&
        (filters.priceMax === undefined ||
          (item.price !== undefined && item.price <= filters.priceMax)) &&
        (filters.surfaceMin === undefined ||
          (item.areaM2 !== undefined && item.areaM2 >= filters.surfaceMin)) &&
        (filters.surfaceMax === undefined ||
          (item.areaM2 !== undefined && item.areaM2 <= filters.surfaceMax)) &&
        (filters.bedroomsMin === undefined ||
          (item.nbBedrooms !== undefined && item.nbBedrooms >= filters.bedroomsMin)) &&
        (filters.bedroomsMax === undefined ||
          (item.nbBedrooms !== undefined && item.nbBedrooms <= filters.bedroomsMax)) &&
        (filters.roomsMin === undefined ||
          (item.nbRooms !== undefined && item.nbRooms >= filters.roomsMin)) &&
        (filters.roomsMax === undefined ||
          (item.nbRooms !== undefined && item.nbRooms <= filters.roomsMax)) &&
        (filters.landSurfaceMin === undefined ||
          (item.landSurface !== undefined && item.landSurface >= filters.landSurfaceMin)) &&
        (filters.landSurfaceMax === undefined ||
          (item.landSurface !== undefined && item.landSurface <= filters.landSurfaceMax)) &&
        (filters.floorNbMin === undefined ||
          (item.floorNb !== undefined && item.floorNb >= filters.floorNbMin)) &&
        (filters.floorNbMax === undefined ||
          (item.floorNb !== undefined && item.floorNb <= filters.floorNbMax)) &&
        (minDpeValue === undefined ||
          (itemDpeValue !== undefined && itemDpeValue >= minDpeValue)) &&
        (maxDpeValue === undefined ||
          (itemDpeValue !== undefined && itemDpeValue <= maxDpeValue)) &&
        (filters.typeLabel === undefined ||
          filters.typeLabel.length === 0 ||
          (item.typeLabel !== undefined && filters.typeLabel.includes(item.typeLabel)))
      );
    });
    return matchingIds.length;
  };

  // Sort searches: default first, then by lastUsed
  const sortedSearches = useMemo(() => {
    return [...searches].sort((a, b) => {
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return b.lastUsed - a.lastUsed;
    });
  }, [searches]);

  return (
    <Wrapper>
      <Title>Recherches sauvegardées</Title>
      <SearchList>
        {sortedSearches.map(search => {
          const isCurrent = search.id === currentSearchId;
          const summary = getFilterSummary(search.filters);
          const resultCount = getResultCount(search.filters);

          return (
            <SearchCard key={search.id} $isCurrent={isCurrent}>
              <Stack spacing={1} sx={{flex: 1}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">{search.name}</Typography>
                    {isCurrent && <Chip label="Active" size="small" color="success" />}
                  </Stack>
                  <Stack direction="row">
                    {!search.isDefault && (
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefault(search)}
                        title="Définir par défaut"
                      >
                        <StarBorder fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => handleRename(search)} title="Renommer">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {!search.isDefault && (
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(search)}
                        title="Supprimer"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {summary.map(text => (
                    <Chip key={text} label={text} size="small" variant="outlined" />
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {resultCount.toLocaleString()} résultats
                </Typography>
                {!isCurrent && (
                  <Button variant="contained" onClick={() => handleApplySearch(search)} fullWidth>
                    Appliquer
                  </Button>
                )}
              </Stack>
            </SearchCard>
          );
        })}
      </SearchList>

      <Stack direction="row" justifyContent="flex-end" sx={{width: '100%', maxWidth: 1200}}>
        <Button variant="outlined" onClick={handleCreateEmptySearch}>
          Nouvelle recherche
        </Button>
      </Stack>

      {/* Rename Dialog */}
      <Dialog open={editingSearch !== null} onClose={() => setEditingSearch(null)}>
        <DialogTitle>Renommer la recherche</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSaveRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSearch(null)}>Annuler</Button>
          <Button onClick={handleSaveRename} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Supprimer la recherche</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer "{deleteConfirm?.name}" ? Cette action est
            irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Annuler</Button>
          <Button
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            variant="contained"
            color="error"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

SearchesPage.displayName = 'SearchesPage';

const HEADER_HEIGHT = 64;
const BOTTOM_NAV_HEIGHT = 64;
const PADDING = 16;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${PADDING}px;
  padding: ${PADDING}px;
  padding-top: ${HEADER_HEIGHT + PADDING}px;
  padding-bottom: ${BOTTOM_NAV_HEIGHT + PADDING}px;

  @media (max-width: 512px) {
    padding: ${PADDING}px;
    padding-top: ${PADDING}px; /* no header on mobile */
    padding-bottom: ${BOTTOM_NAV_HEIGHT + PADDING}px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
  width: 100%;
  max-width: 1200px;
`;

const SearchList = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchCard = styled.div<{$isCurrent: boolean}>`
  background-color: white;
  border: 2px solid ${props => (props.$isCurrent ? '#1976d2' : '#e0e0e0')};
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;
